import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import {
    fetchDijiKolayChartRequest,
    fetchDijiKolayChartSuccess,
    fetchDijiKolayChartFailure,
    fetchClientStatisticsSuccess,
    fetchClientStatisticsFailure,
    fetchClientStatisticsRequest,
    fetchSubscriptionManagementSuccess,
    fetchSubscriptionManagementFailure,
    fetchSubscriptionManagementRequest,
    fetchClientBasedStatisticsRequest,
    fetchClientBasedStatisticsSuccess,
    fetchClientBasedStatisticsFailure,
    fetchAllCompanyNamesRequest,
    fetchAllCompanyNamesSuccess,
    fetchAllCompanyNamesFailure
} from '../reducers/dashboard/dashboardSlice';
import { ClientBasedStatisticsData, ClientStatisticsData, DijiKolayChartItem, SubscriptionManagementData } from '../reducers/dashboard/dashboard.types';
import { getClientBasedStatisticsData, getClientStatisticsData, getDijikolayClientData, getSubscriptionManagementData, getAllCompaniesData } from '../../services/dashboard';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';

function* fetchDijiKolayChartSaga() {
    try {
        const response: { data: { customers: DijiKolayChartItem[] } } = yield call(getDijikolayClientData);
        if (response) {
            yield put(fetchDijiKolayChartSuccess(response.data.customers));
        } else {
            yield put(fetchDijiKolayChartFailure('Dijikolay chart data fetch failed'));
        }
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(fetchDijiKolayChartFailure(error.message));
        } else {
            yield put(fetchDijiKolayChartFailure('An error occurred'));
        }
    }
}

function* fetchClientStatisticsSaga() {
    try {
        const response: { data: ClientStatisticsData, success: boolean } = yield call(getClientStatisticsData);

        if (response) {
            yield put(fetchClientStatisticsSuccess(response.data));
        } else {
            yield put(fetchClientStatisticsFailure('Client Statistics data fetch failed'));
        }
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(fetchClientStatisticsFailure(error.message));
        } else {
            yield put(fetchClientStatisticsFailure('An error occurred'));
        }
    }
}

function* fetchSubscriptionManagementSaga() {
    try {
        const response: { data: SubscriptionManagementData, success: boolean } = yield call(getSubscriptionManagementData);

        if (response) {
            yield put(fetchSubscriptionManagementSuccess(response.data));
        } else {
            yield put(fetchSubscriptionManagementFailure('Subscription management data fetch failed'));
        }
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(fetchSubscriptionManagementFailure(error.message));
        } else {
            yield put(fetchSubscriptionManagementFailure('An error occurred'));
        }
    }
}

function* fetchClientBasedStatisticsSaga(action: PayloadAction<{ page: number; limit: number; companyName?: string[] }>) {
    try {
        const { page, limit, companyName } = action.payload;

        const cacheKey = `p:${page}-l:${limit}-c:${companyName?.join(',') || 'all'}`;

        const cacheState: Record<string, { data: ClientBasedStatisticsData[], total: number }> = yield select(
            (state: RootState) => state.dashboard.clientBaseStatistics.cache
        );
        const cachedData = cacheState[cacheKey];

        if (cachedData) {
            yield put(fetchClientBasedStatisticsSuccess({
                data: cachedData.data,
                total: cachedData.total,
                page,
                limit,
                cacheKey
            }));
            return;
        }

        const response: { data: ClientBasedStatisticsData[], total: number, page: number, limit: number, success: boolean } = yield call(getClientBasedStatisticsData, page, limit, companyName);

        if (response) {
            yield put(fetchClientBasedStatisticsSuccess({
                data: response.data,
                total: response.total,
                page: response.page,
                limit: response.limit,
                cacheKey
            }));
        } else {
            yield put(fetchClientBasedStatisticsFailure('Client based statistics data fetch failed'));
        }
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(fetchClientBasedStatisticsFailure(error.message));
        } else {
            yield put(fetchClientBasedStatisticsFailure('An error occurred'));
        }
    }
}

function* fetchAllCompanyNamesSaga() {
    try {
        const response: { data: { companies: { id: string | number; name: string }[] } } = yield call(getAllCompaniesData);
        if (response && response.data && response.data.companies) {
            const names = response.data.companies.map((company) => company.name);
            const uniqueNames = Array.from(new Set(names)).filter(Boolean);

            yield put(fetchAllCompanyNamesSuccess(uniqueNames));
        } else {
            yield put(fetchAllCompanyNamesFailure('Failed to fetch company names'));
        }
    } catch (error: unknown) {
        if (error instanceof Error) {
            yield put(fetchAllCompanyNamesFailure(error.message));
        } else {
            yield put(fetchAllCompanyNamesFailure('An error occurred'));
        }
    }
}

export function* watchDashboardSaga() {
    yield all([
        takeLatest(fetchDijiKolayChartRequest.type, fetchDijiKolayChartSaga),
        takeLatest(fetchClientStatisticsRequest.type, fetchClientStatisticsSaga),
        takeLatest(fetchSubscriptionManagementRequest.type, fetchSubscriptionManagementSaga),
        takeLatest(fetchClientBasedStatisticsRequest.type, fetchClientBasedStatisticsSaga),
        takeLatest(fetchAllCompanyNamesRequest.type, fetchAllCompanyNamesSaga)
    ]);
}